/**
 * Safely extract a human-readable error message from an Axios error.
 *
 * Handles:
 *  - Pydantic validation errors (detail is an array of {msg, loc, type, …})
 *  - FastAPI string detail  (detail is a plain string)
 *  - Top-level array of validation objects (no detail wrapper)
 *  - Generic JS error .message fallback
 */
export function extractErrorMessage(err: unknown, fallback = 'Something went wrong'): string {
  if (!err || typeof err !== 'object') return fallback;

  const axiosErr = err as any;
  const data = axiosErr?.response?.data;

  if (data) {
    // data.detail may be a string or an array
    const detail = data.detail;

    if (typeof detail === 'string') return detail;

    if (Array.isArray(detail)) {
      const msgs = detail
        .map((item: any) => (typeof item === 'string' ? item : item?.msg))
        .filter(Boolean);
      if (msgs.length) return msgs.join('; ');
    }

    // Some endpoints return { message: "..." } or { error: "..." }
    if (typeof data.message === 'string') return data.message;
    if (typeof data.error === 'string') return data.error;

    // Top-level array (no detail wrapper)
    if (Array.isArray(data)) {
      const msgs = data
        .map((item: any) => (typeof item === 'string' ? item : item?.msg))
        .filter(Boolean);
      if (msgs.length) return msgs.join('; ');
    }
  }

  // Plain JS error
  if (typeof (err as any).message === 'string' && (err as any).message) {
    return (err as any).message;
  }

  return fallback;
}
